/** Subset of `import.meta.env` needed for API base URL resolution. */
export type ApiBaseUrlEnv = {
  VITE_API_BASE_URL?: string
  /** Vite: `true` on `vite dev` */
  DEV?: boolean
  /** Vite: `true` on `vite build` output */
  PROD?: boolean
}

function safeOrigin(loc: Pick<Window, 'location'>['location']): string {
  if (typeof loc.origin === 'string' && loc.origin) {
    return loc.origin
  }
  const { protocol, hostname, port } = loc
  if (!hostname) {
    return ''
  }
  const defaultPort = protocol === 'https:' ? '443' : '80'
  const p =
    port && port !== defaultPort ? `:${port}` : ''
  return `${protocol}//${hostname}${p}`
}

/** In production bundles, ignore a baked-in dev API URL (common monorepo .env mistake). */
function ignoreExplicitInProd(prod: boolean | undefined, explicit: string): boolean {
  if (prod !== true) return false
  try {
    const u = new URL(explicit)
    return (
      u.hostname === 'localhost' ||
      u.hostname === '127.0.0.1' ||
      u.hostname === '[::1]'
    )
  } catch {
    return false
  }
}

/**
 * Resolve API base URL:
 * - Non-local `VITE_API_BASE_URL` wins (trimmed). In **production**, a localhost URL is ignored so a dev `.env` does not break MAX.
 * - Real hostname (not loopback): `location.origin` so requests are absolute same-origin (WebView-safe).
 * - Loopback + Vite ports or `env.DEV`: `http://localhost:3000` for local Nest
 */
export function getApiBaseUrl(
  env: ApiBaseUrlEnv,
  win?: Pick<Window, 'location'>,
): string {
  const rawExplicit = (env?.VITE_API_BASE_URL ?? '').trim()
  if (rawExplicit && !ignoreExplicitInProd(env.PROD, rawExplicit)) {
    return rawExplicit
  }

  const loc = win?.location
  if (!loc) {
    return 'http://localhost:3000'
  }

  const host = loc.hostname || ''
  const port = loc.port || ''

  const onLoopback =
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]'

  const isViteDevPort = port === '5173' || port === '4173'

  if (onLoopback && (isViteDevPort || env.DEV === true)) {
    return 'http://localhost:3000'
  }

  if (host && !onLoopback) {
    return safeOrigin(loc)
  }

  if (loc.protocol === 'https:') {
    return safeOrigin(loc)
  }

  if (onLoopback) {
    return 'http://localhost:3000'
  }

  return ''
}
